import { Component, OnDestroy } from '@angular/core';
import { finalize, forkJoin, of, Subject, switchMap, takeUntil } from 'rxjs';
import {
  GEN_MODEL_OPTIONS,
  ModeChip,
  STUDIO_MODES,
  StudioModelKey,
  StudioOperation,
  SuperImageHistoryItem,
  SuperImageRequest,
  SuperImageResponse
} from './super-image-studio.models';
import { SuperImageStudioService } from '../../services/super-image-studio.service';
import { SuperImageStudioHistoryService } from '../../services/super-image-studio-history.service';

@Component({
  selector: 'app-super-image-studio',
  templateUrl: './super-image-studio.component.html',
  styleUrls: ['./super-image-studio.component.scss']
})
export class SuperImageStudioComponent implements OnDestroy {
  readonly modes: ModeChip[] = STUDIO_MODES;
  readonly genModelOptions = GEN_MODEL_OPTIONS;

  selectedMode: StudioOperation = 'generate';
  selectedGenModel: StudioModelKey = 'sd35_large';

  prompt = '';
  negativePrompt = '';
  aspectRatio = '1:1';
  outputFormat: 'png' | 'jpeg' | 'webp' = 'png';
  seed: number | null = null;
  strength = 0.65;

  baseImageFile: File | null = null;
  maskFile: File | null = null;
  referenceFile: File | null = null;

  basePreviewUrl: string | null = null;
  maskPreviewUrl: string | null = null;
  refPreviewUrl: string | null = null;

  isLoading = false;
  error = '';
  resultUrl: string | null = null;
  resultMeta: { modelId?: string; seed?: number | null; finishReasons?: string[] } = {};

  showSettings = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private studio: SuperImageStudioService,
    private historyService: SuperImageStudioHistoryService
  ) {}

  get activeMode(): ModeChip {
    return this.modes.find((mode) => mode.key === this.selectedMode) ?? this.modes[0];
  }

  get history$() {
    return this.historyService.history$;
  }

  setMode(mode: StudioOperation): void {
    this.selectedMode = mode;
    this.error = '';
    this.resultUrl = null;
    this.resultMeta = {};
  }

  onPickBase(event: Event): void {
    const file = (event.target as HTMLInputElement)?.files?.[0] ?? null;
    this.baseImageFile = file;
    this.basePreviewUrl = this.swapPreviewUrl(this.basePreviewUrl, file);
  }

  onPickMask(event: Event): void {
    const file = (event.target as HTMLInputElement)?.files?.[0] ?? null;
    this.maskFile = file;
    this.maskPreviewUrl = this.swapPreviewUrl(this.maskPreviewUrl, file);
  }

  onPickRef(event: Event): void {
    const file = (event.target as HTMLInputElement)?.files?.[0] ?? null;
    this.referenceFile = file;
    this.refPreviewUrl = this.swapPreviewUrl(this.refPreviewUrl, file);
  }

  shoot(): void {
    this.error = '';
    this.resultUrl = null;

    const mode = this.activeMode;

    if (!this.prompt.trim() && ['generate', 'generate_fast', 'generate_ultra'].includes(this.selectedMode)) {
      this.error = 'Add a prompt to generate an image.';
      return;
    }
    if (mode.requiresImage && !this.baseImageFile) {
      this.error = 'Add a base image for this mode.';
      return;
    }
    if (mode.requiresMask && !this.maskFile) {
      this.error = 'Add a mask image for this mode.';
      return;
    }
    if (mode.requiresRef && !this.referenceFile) {
      this.error = 'Add a reference image for this mode.';
      return;
    }

    this.isLoading = true;

    const base$ = this.baseImageFile ? this.studio.fileToBase64(this.baseImageFile) : of<string | null>(null);
    const mask$ = this.maskFile ? this.studio.fileToBase64(this.maskFile) : of<string | null>(null);
    const ref$ = this.referenceFile ? this.studio.fileToBase64(this.referenceFile) : of<string | null>(null);

    forkJoin([base$, mask$, ref$])
      .pipe(
        switchMap(([baseB64, maskB64, refB64]) => {
          const request: SuperImageRequest = {
            operation: this.selectedMode,
            prompt: this.prompt.trim() || undefined,
            negative_prompt: this.negativePrompt.trim() || undefined,
            aspect_ratio: this.aspectRatio,
            output_format: this.outputFormat,
            seed: typeof this.seed === 'number' ? this.seed : undefined,
            strength: this.strength,
            image: baseB64 || undefined,
            mask: maskB64 || undefined,
            reference_image: refB64 || undefined
          };

          if (this.selectedMode === 'generate') request.modelKey = this.selectedGenModel;
          if (this.selectedMode === 'generate_fast') request.modelKey = 'stable_core';
          if (this.selectedMode === 'generate_ultra') request.modelKey = 'stable_ultra';

          return this.studio.run(request);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: SuperImageResponse) => {
          if (response.error) {
            this.error = response.error;
            this.resultMeta = { finishReasons: response.finish_reasons };
            return;
          }

          this.resultUrl = this.studio.buildDataUrl(response.image, response.output_format);
          this.resultMeta = { modelId: response.model_id, seed: response.seed ?? null };

          const historyItem: SuperImageHistoryItem = {
            id: crypto.randomUUID(),
            operation: this.selectedMode,
            modelId: response.model_id,
            prompt: this.prompt.trim() || undefined,
            negativePrompt: this.negativePrompt.trim() || undefined,
            aspectRatio: this.aspectRatio,
            outputFormat: response.output_format,
            seed: response.seed ?? null,
            strength: this.strength,
            createdAt: new Date().toISOString(),
            image: this.resultUrl
          };

          this.historyService.addHistory(historyItem);
        },
        error: (error) => {
          this.error = error?.error?.error || error?.message || 'Failed to generate image.';
        }
      });
  }

  toggleSettings(): void {
    this.showSettings = !this.showSettings;
  }

  clearAll(): void {
    this.prompt = '';
    this.negativePrompt = '';
    this.seed = null;
    this.resultUrl = null;
    this.resultMeta = {};
    this.error = '';
    this.baseImageFile = null;
    this.maskFile = null;
    this.referenceFile = null;
    this.basePreviewUrl = this.revokePreviewUrl(this.basePreviewUrl);
    this.maskPreviewUrl = this.revokePreviewUrl(this.maskPreviewUrl);
    this.refPreviewUrl = this.revokePreviewUrl(this.refPreviewUrl);
  }

  useHistory(item: SuperImageHistoryItem): void {
    this.resultUrl = item.image;
    this.prompt = item.prompt ?? '';
    this.negativePrompt = item.negativePrompt ?? '';
    this.aspectRatio = item.aspectRatio ?? '1:1';
    this.outputFormat = item.outputFormat ?? 'png';
    this.seed = item.seed ?? null;
    this.strength = item.strength ?? 0.65;
    this.setMode(item.operation);
  }

  clearHistory(): void {
    this.historyService.clearHistory();
  }

  private swapPreviewUrl(current: string | null, file: File | null): string | null {
    this.revokePreviewUrl(current);
    return file ? URL.createObjectURL(file) : null;
  }

  private revokePreviewUrl(url: string | null): null {
    if (url) {
      URL.revokeObjectURL(url);
    }
    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokePreviewUrl(this.basePreviewUrl);
    this.revokePreviewUrl(this.maskPreviewUrl);
    this.revokePreviewUrl(this.refPreviewUrl);
  }
}
